import React, {memo, useCallback, useMemo, useState} from 'react';
import moment from 'moment';
import _ from 'lodash';
import clsx from 'clsx';
import Typography from '@mui/material/Typography';
import Grid from '@mui/material/Grid';
import makeStyles from '@mui/styles/makeStyles';
import PropTypes from 'prop-types';
import {useTranslation} from 'react-i18next';
import {ListItemIcon, Stack, Tooltip} from '@mui/material';
import IconButton from '@mui/material/IconButton';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import {useDispatch, useSelector} from 'react-redux';
import {barsContextActive, modifyReview} from '../../../../../../../../../../store/reducers/reviews';
import useOpenState from '../../../../../../../../../../hooks/useOpenState';
import {
  calculateBarsPatientAge,
  getBarsAppointmentReason,
  getBarsAppointmentSlotStart,
  getBarsPatientName,
} from '../../../../utils/barsUtils';
import {getPatientName} from '../../../../../../../../../bedManagement/bmUtils';
import {calculatePatientAge} from '../../../../../../../../../../util/patientUtil';
import {getChiefComplaint, getReviewData} from '../../../../../../utils/edReviewUtils';
import BarsTimer from '../../../../reusable/barsTimer';
import MessageBox from '../../../../../../../../../../util/messageBox';
import EdReviewStatus from '../../../../reusable/edReviewStatus';

const useStyles = makeStyles(() => ({
  ptContextRoot: {
    maxWidth: "100%",
    flexBasis: "100%",
    alignContent: 'flex-start'
  },
  dataRow: {
    gap: 5,
    fontSize: ".9rem",
    alignItems: "center",
    flexBasis: "fit-content",

    "&.firstRow": {
      position: "relative",
    },
    "&.lastRow": {
      flexGrow: 1,
      display: "flex",
      flexBasis: "100%",
      alignItems: "center",
      marginBottom: "0 !important",
      justifyContent: "space-between",
    },
  },
  firstLine: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
  },
  gender: {
    right: -45,
    bottom: -7,
    display: "flex",
    marginLeft: 1,
    marginRight: 8,
    color: "#222425",
    borderRadius: 6,
    transform: "scale(.55)",
    justifyContent: "center",

    "&.male": {
      backgroundColor: "#92bfe2",
    },
    "&.female": {
      backgroundColor: "#f4bdd1",
    },
  },
  textRow: {
    overflow: "hidden",
    maxWidth: "100%",
    whiteSpace: "nowrap",
    textOverflow: "ellipsis",
  },
  menuItem: {
    fontSize: "small",
    width: "100%",
    minWidth: 100,
    display: "flex",
    justifyContent: "flex-start",
  },
  barsTimer: {
    gap: 0,
    alignItems: "flex-end",

    "& .MuiTypography-root": {
      fontWeight: 500,
      minWidth: "fit-content",
    },
  },
}));

const PatientInfoBox = ({
  ptInWard,
  noChiefComplaint,
  calendarView,
  noReviewReason,
  noReviewTimer,
  noAppointmentStatus,
}) => {
  const classes = useStyles();
  const { t } = useTranslation();
  const dispatch = useDispatch();

  const barsCtxActive = useSelector(barsContextActive);

  const [isModalOpen, openModal, closeModal] = useOpenState(false, true);

  const [menuEl, setMenuEl] = useState(null);
  const open = Boolean(menuEl);

  // CONTEXT-BASED PATIENT DATA
  const patientName = useMemo(
    () =>
      barsCtxActive ? getBarsPatientName(ptInWard) : getPatientName(ptInWard),
    [barsCtxActive, ptInWard],
  );
  const patientAge = useMemo(
    () =>
      barsCtxActive
        ? calculateBarsPatientAge(ptInWard)
        : calculatePatientAge(ptInWard),
    [barsCtxActive, ptInWard],
  );
  const appointmentReason = useMemo(
    () =>
      barsCtxActive
        ? getBarsAppointmentReason(ptInWard)
        : getReviewData(ptInWard)?.reason || "-",
    [barsCtxActive, ptInWard],
  );
  const appointmentDate = useMemo(
    () =>
      barsCtxActive
        ? getBarsAppointmentSlotStart(ptInWard)
        : getReviewData(ptInWard)?.date,
    [barsCtxActive, ptInWard],
  );
  const patientGender = useMemo(
    () => _.lowerCase(ptInWard?.patient?.gender),
    [ptInWard],
  );

  const handleOpenMenu = (event) => {
    setMenuEl(event.currentTarget);
  };

  const handleCloseMenu = (e) => {
    const tag = e?.target?.tagName?.toLowerCase();
    if (tag !== "input" && tag !== "svg") {
      setMenuEl(null);
    }
  };

  const getConfirmationModalMessage = useCallback(() => {
    const appointmentDateString = moment(getReviewData(ptInWard)?.date).format(
      "Do MMM h:mma",
    );

    return `Are you sure you want to cancel ${getPatientName(ptInWard)}'s appointment on ${appointmentDateString}?`;
  }, [ptInWard]);

  const onAcceptConfirmation = useCallback(() => {
    const modifiedValues = getReviewData(ptInWard);
    modifiedValues.cancelled = true;
    modifiedValues.status = "cancelled";
    modifiedValues.date = moment(modifiedValues?.date);

    return dispatch(
      modifyReview({
        key: "bedas",
        modifiedValuesObject: modifiedValues,
        originalObject: getReviewData(ptInWard),
        patientId: ptInWard?.__id,
        onAfterSave: closeModal,
        onError: closeModal,
      }),
    );
  }, [ptInWard]);

  return !ptInWard ? (
    <Typography
      style={{ display: "block", marginBottom: "auto" }}
      variant="subtitle2"
    >
      {t("No patient data available.")}
    </Typography>
  ) : (
    <>
      <Grid
        container
        className={clsx(classes.ptContextRoot, "scroll-normal-white")}
      >
        <Grid item xs={12} className={classes.firstLine}>
          <Typography variant="subtitle2" fontWeight={600} fontSize="1rem">
            {patientName}
          </Typography>

          {calendarView && !barsCtxActive && (
            <IconButton
              size="small"
              onClick={handleOpenMenu}
              style={{ height: 25, width: 25 }}
            >
              <MoreVertIcon />
            </IconButton>
          )}
        </Grid>

        <Grid
          container
          item
          xs={12}
          className={clsx(classes.dataRow, "firstRow")}
          justifyContent="space-between"
          style={{ flexBasis: "100%" }}
        >
          {ptInWard?.patient?.hospitalNumber && (
            <Stack
              direction="row"
              alignItems="center"
              justifyContent="space-between"
              gap={1}
              style={{ marginRight: "auto" }}
            >
              <Typography variant="text2" style={{ minWidth: 100 }}>
                Hospital No.
              </Typography>

              <Typography variant="text2" style={{ marginRight: "auto" }}>
                {ptInWard?.patient?.hospitalNumber}
              </Typography>
            </Stack>
          )}

          {calendarView && (
            <BarsTimer
              targetTime={moment(appointmentDate).toISOString()}
              boxDirection="column"
              showDate={false}
              rootClasses={clsx(classes.barsTimer)}
            />
          )}
        </Grid>

        <Grid item xs={12}>
          <Stack
            direction="row"
            alignItems="center"
            gap={1}
            style={{ marginRight: "auto" }}
            flexWrap="nowrap"
          >
            <Typography variant="text2" style={{ minWidth: 100 }}>
              Age
            </Typography>

            <Typography variant="text2" style={{ marginRight: "auto" }}>
              {patientAge}
            </Typography>

            {!noAppointmentStatus && (
              <EdReviewStatus
                patient={ptInWard}
                hospitalNumber={ptInWard?.patient?.hospitalNumber}
              />
            )}
          </Stack>
        </Grid>

        {!noChiefComplaint && !barsCtxActive && (
          <Grid
            container
            item
            xs={12}
            className={clsx(classes.dataRow, "lastRow")}
          >
            <Tooltip title={`Chief Complaint: ${getChiefComplaint(ptInWard)}`}>
              <Typography
                style={{ display: "inline-block" }}
                variant="text2"
                className={classes.textRow}
              >
                CC: {getChiefComplaint(ptInWard)}
              </Typography>
            </Tooltip>
          </Grid>
        )}

        {(!noReviewTimer || !noReviewTimer) && (
          <Grid
            container
            item
            xs={12}
            className={clsx(classes.dataRow, "lastRow")}
          >
            {!noReviewTimer && (
              <Typography style={{ display: "inline-block" }} variant="text2">
                {t(`Reason: ${appointmentReason}`)}
              </Typography>
            )}
          </Grid>
        )}
      </Grid>

      <Menu
        anchorEl={menuEl}
        id="ward-panel-menu"
        open={open}
        onClose={handleCloseMenu}
        onClick={handleCloseMenu}
        PaperProps={{
          elevation: 0,
          sx: {
            overflow: "visible",
            bgcolor: "background.paperDarker",

            p: 0.5,
            borderRadius: "10px",
            "& .MuiAvatar-root": {
              width: 32,
              height: 32,
              ml: -1.5,
              mr: 1,
            },
          },
        }}
        BackdropProps={{
          invisible: true,
        }}
        transformOrigin={{ horizontal: "right", vertical: "top" }}
        anchorOrigin={{ horizontal: "right", vertical: "bottom" }}
      >
        <MenuItem
          className={classes.menuItem}
          disableRipple
          // onClick={() => console.log("TODO Rescheduling")}
          disabled={true}
        >
          <ListItemIcon>
            <TrendingUpIcon />
          </ListItemIcon>
          {t("Reschedule")}
        </MenuItem>

        <MenuItem
          className={classes.menuItem}
          disableRipple
          disabled={false}
          onClick={openModal}
          // disabled={!dispatch(hasPermission(Permissions.AGYLE_CAN_ADMIT_PATIENT))}
        >
          <ListItemIcon>
            <PersonAddIcon />
          </ListItemIcon>
          {t("Cancel Appointment")}
        </MenuItem>
      </Menu>

      <MessageBox
        bookAdmission
        onClose={closeModal}
        open={!!isModalOpen}
        title={t("Confirmation")}
        onAccept={onAcceptConfirmation}
        haveAccept={{ yes: t("Submit"), no: t("Close") }}
        messageObject={t(getConfirmationModalMessage())}
      />
    </>
  );
};

PatientInfoBox.defaultProps = {
  ptInWard: null,
  noReviewTimer: false,
  noReviewReason: false,
  noAppointmentStatus: false,
  noChiefComplaint: false,
};

PatientInfoBox.propTypes = {
  noReviewTimer: PropTypes.bool,
  noReviewReason: PropTypes.bool,
  noChiefComplaint: PropTypes.bool,
  noAppointmentStatus: PropTypes.bool,
  ptInWard: PropTypes.oneOfType([PropTypes.object, PropTypes.bool]),
};

export default memo(PatientInfoBox, _.isEqual);
